import { Box, useMediaQuery, useTheme } from "@mui/material";

import { useSidebar } from "@/contexts/SidebarContext";

const FULL_DRAWER_WIDTH = 240;
const ICON_COLUMN_WIDTH = 56;

interface IBottomFixedActionContainerProps {
  buttonGroups: JSX.Element[][];
}

const BottomFixedActionContainer = ({
  buttonGroups,
}: IBottomFixedActionContainerProps) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery("(min-width: 1260px)"); // 1260px+
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md")); // 900px+
  const isSmallScreen = useMediaQuery(theme.breakpoints.up("sm")); // 600px+
  const isTabletScreen = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600px-900px

  const { collapsed } = useSidebar();
  const sidebarOffset = isSmallScreen
    ? collapsed
      ? ICON_COLUMN_WIDTH
      : FULL_DRAWER_WIDTH
    : 0;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: sidebarOffset,
        right: 0,
        transition: "left 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        padding: isLargeScreen
          ? "8px 60px"
          : isSmallScreen
            ? "8px 20px"
            : "8px 12px",
        backgroundColor: "background.paper",
        borderTop: "1px solid var(--divider)",
        zIndex: 1300,
        minHeight: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        boxShadow: "0px -2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden", // Prevent horizontal overflow
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: isLargeScreen ? "space-between" : "center",
          alignItems: "center",
          width: "100%",
          maxWidth: "100%",
          gap: isSmallScreen ? "8px" : "4px",
          flexWrap: "wrap",
          overflow: "hidden",
        }}
      >
        {buttonGroups.map((buttonGroup, groupIndex) => (
          <Box
            key={`button-group-${groupIndex}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isSmallScreen ? "8px" : "4px",
              flexWrap: "wrap",
              justifyContent: isLargeScreen
                ? groupIndex === 0
                  ? "flex-start"
                  : "flex-end"
                : "center",
              maxWidth: "100%",
            }}
          >
            {buttonGroup.map((button, buttonIndex) => (
              <Box
                key={`button-${buttonIndex}`}
                sx={{
                  flexShrink: 0,
                  "& .MuiButton-root": {
                    fontSize: isLargeScreen
                      ? "12px"
                      : isTabletScreen
                        ? "10px"
                        : isMediumScreen
                          ? "11px"
                          : "9px",
                    padding: isLargeScreen
                      ? "8px 16px"
                      : isTabletScreen
                        ? "6px 10px"
                        : isMediumScreen
                          ? "6px 12px"
                          : "0",
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    textTransform: "uppercase",
                    fontWeight: "600",
                    borderRadius: isSmallScreen ? "6px" : "50%",
                    width: isSmallScreen ? "auto" : "40px",
                    height: isSmallScreen ? "auto" : "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "& .MuiButton-endIcon": {
                      marginLeft: isSmallScreen
                        ? isTabletScreen
                          ? "6px"
                          : "8px"
                        : "0",
                      marginRight: "0",
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: isSmallScreen
                        ? isTabletScreen
                          ? "6px"
                          : "8px"
                        : "0",
                      marginLeft: "0",
                    },
                    // Hide text on small screens
                    "& .MuiButton-startIcon + *": {
                      display: isSmallScreen ? "inline" : "none",
                    },
                  },
                  "& .MuiIconButton-root": {
                    width: isSmallScreen ? "32px" : "40px",
                    height: isSmallScreen ? "32px" : "40px",
                  },
                }}
              >
                {button}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BottomFixedActionContainer;
